// `TaskExpired` is emitted by AIGC/MT when a task's TTL is exceeded. Per
// the AgenticOnboarding design doc the canonical wire form is an HTTP 400
// (a non-transient rejection, handled by the .catch branch below as
// `onFailed`), but we also union it into the body-status enum so a future MT
// change that surfaces it inline in the response body doesn't silently fall
// into the InProgress branch and spin the analyzing view forever. AB#10747343.
export type TaskStatus = 'InProgress' | 'Completed' | 'Failed' | 'TaskExpired';

export interface TaskPollData {
  Status?: TaskStatus;
  [key: string]: unknown;
}

export interface TaskStatusPollerConfig {
  pollTask: () => Promise<unknown>;
  onInProgress?: (data: TaskPollData) => void;
  onCompleted: (data: TaskPollData) => void;
  onFailed: (data?: TaskPollData) => void;
  interval?: number;
  // How long a run of consecutive transient poll failures (see
  // `isTransientPollError`) is tolerated before the task is reported failed.
  transientErrorWindowMs?: number;
}

export interface TaskStatusPoller {
  start: () => void;
  stop: () => void;
}

// Long enough to ride out a dropped connection, short enough that a
// persistent failure still reaches the error view instead of an endless
// spinner.
const DEFAULT_TRANSIENT_ERROR_WINDOW_MS = 30000;

// A rejected poll only says the *request* failed, not that the task did. It is
// retried only when positively identified as a transport / availability
// problem:
//   0              no HTTP response at all (offline, connection reset, blocked
//                  or aborted request, client timeout)
//   408, 429       request timeout / throttled
//   502, 503, 504  gateway or service temporarily unavailable
// Everything else stays terminal — notably the backend's own verdicts, HTTP 400
// (TaskExpired / TaskNotFound) and 401. HTTP 500 is terminal too until the
// backend owners confirm whether an AIGCException means the task failed.
// AB#11284461.
const TRANSIENT_HTTP_STATUSES = new Set([0, 408, 429, 502, 503, 504]);

export const isTransientPollError = (error: unknown): boolean => {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' && TRANSIENT_HTTP_STATUSES.has(status);
};

const extract = (result: unknown): TaskPollData => {
  if (result == null) return {};
  const r = result as any;
  const raw = r.data !== undefined ? r.data : r;
  return (raw?.Data ?? raw ?? {}) as TaskPollData;
};

export const createTaskStatusPoller = ({
  pollTask,
  onInProgress,
  onCompleted,
  onFailed,
  interval = 2000,
  transientErrorWindowMs = DEFAULT_TRANSIENT_ERROR_WINDOW_MS,
}: TaskStatusPollerConfig): TaskStatusPoller => {
  let timerId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  let pollInFlight = false;
  // Start of the current run of consecutive transient poll failures; cleared
  // by any poll that gets an answer back.
  let transientErrorSince: number | null = null;

  const stopTimer = () => {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
  };

  const fail = (data?: TaskPollData) => {
    if (stopped) return;
    stopped = true;
    stopTimer();
    onFailed(data);
  };

  const handleResult = (data: TaskPollData) => {
    const status = data?.Status;
    if (status === 'Completed') {
      stopTimer();
      stopped = true;
      onCompleted(data);
    } else if (status === 'Failed' || status === 'TaskExpired') {
      // TaskExpired is a terminal state — same teardown as Failed.
      // Caller's onFailed surfaces the error view; the existing "Try again"
      // button on the snapshot-review error view already clears the stale
      // snapshotTaskId and routes back to Step 1 so a fresh task can be
      // generated.
      fail(data);
    } else if (onInProgress) {
      onInProgress(data);
    }
  };

  const poll = () => {
    if (stopped || pollInFlight) return;
    pollInFlight = true;

    pollTask().then((result) => {
      pollInFlight = false;
      if (stopped) return;
      transientErrorSince = null;
      handleResult(extract(result));
    }).catch((error: unknown) => {
      pollInFlight = false;
      if (isTransientPollError(error)) {
        const now = Date.now();
        transientErrorSince = transientErrorSince ?? now;
        // Leave the interval running: the next tick polls the same task again.
        if (now - transientErrorSince < transientErrorWindowMs) return;
      }
      fail();
    });
  };

  const start = () => {
    if (stopped || timerId !== null) return;
    poll();
    timerId = setInterval(poll, interval);
  };

  const stop = () => {
    stopped = true;
    stopTimer();
  };

  return { start, stop };
};
